import {parseNumber} from '@benchling/js-utils';
import {Box, Button, Group, Input, TextInput, createStyles} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {getNumericValidationError, isInvalidNumericValue} from 'ui-library/inputs/NumericInputValidation';
import {NumericInputChangeEvent} from 'util/Types';

export interface InlineAction {
  label: string;
  disabled: boolean;
  onClick: () => void;
}

export interface NumericInputWithActionProps {
  value?: string | number | null;
  onChange: (e: NumericInputChangeEvent) => void;
  isDisabled?: boolean;
  required?: boolean;
  min?: number;
  minIsInclusive?: boolean;
  max?: number;
  maxIsInclusive?: boolean;
  validateInteger?: boolean;
  placeholder?: string;
  id?: string;
  action?: InlineAction;
  dataTestAttrs?: DataTestAttrs;
}

export default function NumericInputWithAction({
  value,
  onChange,
  isDisabled = false,
  required = false,
  min,
  minIsInclusive = true,
  max,
  maxIsInclusive = true,
  validateInteger = false,
  placeholder,
  id,
  action,
  dataTestAttrs = {component: 'NumericInputWithAction', element: 'root'},
}: NumericInputWithActionProps): JSX.Element {
  const [displayValue, setDisplayValue] = useState((value ?? '').toString());
  const previousValueRef = useRef(value);
  const inputId = useId(id);
  const errorId = `${inputId}-error`;

  useEffect(() => {
    if (previousValueRef.current !== value && parseNumber(displayValue) !== value) {
      setDisplayValue((value ?? '').toString());
    }
    previousValueRef.current = value;
  }, [displayValue, value]);

  const errorMessage = useMemo(
    () =>
      getNumericValidationError({
        value: displayValue,
        required,
        validateInteger,
        min,
        minIsInclusive,
        max,
        maxIsInclusive,
      }),
    [displayValue, max, maxIsInclusive, min, minIsInclusive, required, validateInteger]
  );
  const {classes, cx} = useStyles();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const nextDisplayValue = e.target.value;
    const nextNumericValue = parseNumber(nextDisplayValue);
    const nextIsInvalid = isInvalidNumericValue({
      value: nextDisplayValue,
      required,
      validateInteger,
      min,
      minIsInclusive,
      max,
      maxIsInclusive,
    });
    const numericValue = nextIsInvalid ? null : nextNumericValue;

    setDisplayValue(nextDisplayValue);
    onChange({
      target: {
        value: nextDisplayValue,
        isValid: !nextIsInvalid,
        numericValue,
      },
    });
  };

  // When there is no action, render a plain TextInput that owns its own error
  // message, matching standard input behavior.
  if (action == null) {
    return (
      <TextInput
        value={displayValue}
        onChange={handleChange}
        id={inputId}
        disabled={isDisabled}
        placeholder={placeholder}
        size="md"
        error={errorMessage ?? undefined}
        wrapperProps={getTestProps(dataTestAttrs)}
        {...getTestProps({component: 'NumericInputWithAction', element: 'input'})}
      />
    );
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && !action.disabled && !isDisabled) {
      e.preventDefault();
      action.onClick();
    }
  };

  // With an action, render the input and the button as flush siblings so the
  // input's (error) border is confined to the input and never bleeds under or
  // beyond the button. The error message is rendered below the whole field.
  return (
    <Box {...getTestProps(dataTestAttrs)}>
      <Group spacing={0} align="stretch" noWrap={true} className={classes.fieldRow}>
        <TextInput
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          id={inputId}
          disabled={isDisabled}
          placeholder={placeholder}
          size="md"
          error={errorMessage ?? undefined}
          inputWrapperOrder={['input']}
          classNames={{root: classes.inputRoot, input: cx(classes.inputWithAction)}}
          {...getTestProps({component: 'NumericInputWithAction', element: 'input'})}
        />
        <Button
          variant="primary"
          className={classes.actionButton}
          disabled={action.disabled || isDisabled}
          onClick={action.onClick}
          type="button"
          {...getTestProps({component: 'NumericInputWithAction', element: 'actionButton'})}
        >
          {action.label}
        </Button>
      </Group>
      {errorMessage != null && (
        <Input.Error id={errorId} className={classes.errorText}>
          {errorMessage}
        </Input.Error>
      )}
    </Box>
  );
}

const useStyles = createStyles(() => ({
  fieldRow: {
    width: '100%',
  },
  inputRoot: {
    flex: 1,
    minWidth: 0,
  },
  inputWithAction: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  actionButton: {
    flexShrink: 0,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    // Overlap the input's right border so the seam is a single 1px line.
    marginLeft: -1,
    whiteSpace: 'nowrap',
    // Override the theme's fixed button height so flexbox `align="stretch"`
    // sizes the button to match the input's height. The doubled `&&` selector
    // raises specificity above the theme's component styles.
    '&&': {
      height: 'auto',
      minHeight: 0,
    },
  },
  errorText: {
    marginTop: 4,
  },
}));
